/**
 * GamePage.tsx — Open Game page (G04 realtime; H04 navigation +
 * permission-driven controls).
 *
 * ONE page for creator and normal player (H04): control visibility is
 * driven exclusively by the backend-computed `game.permissions` — the
 * client never infers role/creator status locally, and the server remains
 * the final authority for every action. `userId` is display-only (finding
 * "you" in the player list); it is never sent as an authorization claim.
 *
 * Realtime: initial state via HTTP; every validated `game:changed` triggers
 * an authoritative refetch, so joins/ready/starts appear without a manual
 * reload. I01 completes the shell (invite code, budget, end date, secret
 * persons); I02 adds the Copy Invite control (Clipboard API +
 * execCommand fallback); I04–I08 add the wishlist/reveal overlays on the
 * same permission flags.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiClientError, type PlayerDto } from '@shared';
import { apiV1 } from '../api';
import { formatBudgetCents } from '../format';
import { ManageWishlistOverlay } from '../overlays/ManageWishlistOverlay';
import { PlayerWishlistOverlay } from '../overlays/PlayerWishlistOverlay';
import { RevealAssignmentsOverlay } from '../overlays/RevealAssignmentsOverlay';
import { SnowOverlay } from '../overlays/SnowOverlay';
import { useGameRealtime, type UseGameRealtimeOptions } from '../useGameRealtime';

/**
 * Overlays hosted by the page (I04 manage wishlist; I05 player wishlist;
 * I07 reveal). Opening an overlay is the ONLY moment its data is fetched —
 * in particular the I07 reveal is never preloaded into page state.
 */
type GameOverlayState =
  | { type: 'manageWishlist' }
  | { type: 'playerWishlist'; player: PlayerDto }
  | { type: 'reveal' }
  | { type: 'snow' }
  | null;

interface GamePageProps extends Pick<UseGameRealtimeOptions, 'socketFactory' | 'onGameChanged'> {
  gameId: number;
  /** Session user id — finds "you" in the player list (display only). */
  userId: number;
  onBack: () => void;
}

export function GamePage({ gameId, userId, onBack, socketFactory, onGameChanged }: GamePageProps) {
  const { game, loading, error, stale, realtimeStatus, refetch } = useGameRealtime(gameId, {
    socketFactory,
    onGameChanged: (reason) => {
      setChangeTick((t) => t + 1);
      onGameChanged?.(reason);
    },
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const [acting, setActing] = useState<'ready' | 'start' | null>(null);
  // K02: synchronous duplicate-submit guard — the `acting` STATE can't stop
  // a same-tick second click (stale closure), which would fire a second
  // POST (e.g. a duplicate /start that the server rejects with 409).
  const actingRef = useRef<'ready' | 'start' | null>(null);
  const [overlay, setOverlay] = useState<GameOverlayState>(null);
  // I04: bumped on every game:changed so an open overlay refetches its data.
  const [changeTick, setChangeTick] = useState(0);
  // I02: visual "Copied" feedback for the invite copy control.
  const [copiedInvite, setCopiedInvite] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  if (loading && !game) {
    return (
      <section className="game-page">
        <p aria-live="polite">Loading game…</p>
      </section>
    );
  }

  if (error && !game) {
    return (
      <section className="game-page">
        <p role="alert">{error}</p>
        <button type="button" className="auth-submit" onClick={onBack}>
          Back
        </button>
      </section>
    );
  }

  if (!game) return null;

  const self = game.players.find((p) => p.userId === userId);
  const { canManageWishlist, canMarkReady, canStartGame, canRevealAssignments } = game.permissions;

  /** Fallback for contexts without the async Clipboard API (e.g. http). */
  function copyViaExecCommand(text: string): void {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    el.remove();
    if (!ok) throw new Error('copy failed');
  }

  async function handleCopyInvite(): Promise<void> {
    const code = game!.inviteCode;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        copyViaExecCommand(code);
      }
    } catch {
      try {
        copyViaExecCommand(code);
      } catch {
        // Clipboard unavailable — the user can select the code manually.
        return;
      }
    }
    setCopiedInvite(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopiedInvite(false), 2000);
  }

  async function handleMarkReady(): Promise<void> {
    if (actingRef.current !== null) return;
    actingRef.current = 'ready';
    setActing('ready');
    setActionError(null);
    try {
      await apiV1.markReady(game!.id);
      refetch(); // your own row refreshes from the authoritative state
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Could not update readiness.');
    } finally {
      actingRef.current = null;
      setActing(null);
    }
  }

  async function handleStart(): Promise<void> {
    if (actingRef.current !== null) return;
    actingRef.current = 'start';
    setActing('start');
    setActionError(null);
    try {
      await apiV1.startGame(game!.id);
      refetch(); // the 'started' realtime event also refreshes everyone
    } catch (err) {
      setActionError(err instanceof ApiClientError ? err.message : 'Could not start the game.');
    } finally {
      actingRef.current = null;
      setActing(null);
    }
  }

  return (
    <section className="game-page" aria-label={`Game ${game.id}`}>
      <header className="game-page-header">
        <button type="button" className="game-back" onClick={onBack}>
          ← Back
        </button>
        <h2>{game.name || `Game #${game.id}`}</h2>
        <span
          className={`game-live game-live--${realtimeStatus ?? 'off'}`}
          aria-label={`Live updates: ${realtimeStatus ?? 'off'}`}
        >
          {realtimeStatus === 'live' ? 'live' : realtimeStatus === 'connecting' ? '…' : 'paused'}
        </span>
      </header>

      {realtimeStatus === 'forbidden' && (
        <p className="game-note" role="alert">
          You no longer have access to live updates for this game.
        </p>
      )}
      {realtimeStatus === 'unavailable' && (
        <p className="game-note">
          Live updates are temporarily unavailable — the page keeps loading the latest state.
        </p>
      )}
      {stale && (
        // K02: a failed background refresh is visible, not silent — the last
        // loaded state stays on screen; the next event or manual Refresh retries.
        <p className="game-note" aria-live="polite">
          Refresh failed — showing the last loaded state. Use Refresh to try again.
        </p>
      )}

      <p className="game-status">
        Status: <strong>{game.status}</strong>
      </p>

      <dl className="game-info">
        <div className="game-info-row">
          <dt>Invite code</dt>
          <dd className="game-invite">
            <span className="game-invite-code">{game.inviteCode}</span>
            <button
              type="button"
              className={copiedInvite ? 'game-copy copied' : 'game-copy'}
              onClick={() => void handleCopyInvite()}
              aria-label="Copy invite code"
            >
              {copiedInvite ? 'Copied' : 'Copy'}
            </button>
          </dd>
        </div>
        <div className="game-info-row">
          <dt>Gift budget</dt>
          <dd>{formatBudgetCents(game.giftBudgetCents)}</dd>
        </div>
        <div className="game-info-row">
          <dt>End date</dt>
          <dd>{game.endDate}</dd>
        </div>
        <div className="game-info-row">
          <dt>Secret persons per player</dt>
          <dd>{game.secretPersonCount}</dd>
        </div>
      </dl>

      <h3>Players</h3>
      <ul className="game-player-list">
        {game.players.map((player) => (
          <li key={player.userId} className={player.isReady ? 'is-ready' : ''}>
            {/* I05: clicking a player opens their read-only wishlist. */}
            <button
              type="button"
              className="game-player-name"
              onClick={() => setOverlay({ type: 'playerWishlist', player })}
              aria-label={`View ${player.username}'s wishlist`}
            >
              {player.username}
            </button>
            <span
              className="game-ready-flag"
              aria-label={`${player.username} ${player.isReady ? 'is ready' : 'is not ready'}`}
            >
              {player.isReady ? '✓ ready' : 'not ready'}
            </span>
          </li>
        ))}
      </ul>

      {(canManageWishlist || canMarkReady || canStartGame || canRevealAssignments) && (
        <div className="game-controls">
          {canManageWishlist && (
            <button
              type="button"
              className="game-refresh"
              onClick={() => setOverlay({ type: 'manageWishlist' })}
            >
              Manage wishlist
            </button>
          )}
          {canMarkReady &&
            (self?.isReady ? (
              <span className="game-ready-note">You are ready ✓</span>
            ) : (
              <button
                type="button"
                className="game-refresh"
                onClick={() => void handleMarkReady()}
                disabled={acting !== null}
              >
                {acting === 'ready' ? 'Marking…' : 'Mark me as ready'}
              </button>
            ))}
          {canStartGame && (
            <button
              type="button"
              className="auth-submit"
              onClick={() => void handleStart()}
              disabled={acting !== null}
            >
              {acting === 'start' ? 'Starting…' : 'Start game'}
            </button>
          )}
          {/* I07: reveal is user-initiated; the page never preloads the
              assignments (secrecy — F05/I07 acceptance). */}
          {canRevealAssignments && (
            <button
              type="button"
              className="auth-submit"
              onClick={() => setOverlay({ type: 'reveal' })}
            >
              Reveal my secret persons
            </button>
          )}
          {actionError && (
            <p className="auth-error" role="alert">
              {actionError}
            </p>
          )}
        </div>
      )}

      <button type="button" className="game-refresh" onClick={refetch}>
        Refresh
      </button>

      {overlay?.type === 'manageWishlist' && (
        <ManageWishlistOverlay
          gameId={game.id}
          budgetCents={game.giftBudgetCents}
          changeTick={changeTick}
          onClose={() => setOverlay(null)}
          inviteCode={game.inviteCode}
          isCreator={game.role === 'Creator'}
          onSnow={() => setOverlay({ type: 'snow' })}
        />
      )}
      {overlay?.type === 'playerWishlist' && (
        <PlayerWishlistOverlay
          gameId={game.id}
          player={overlay.player}
          changeTick={changeTick}
          onClose={() => setOverlay(null)}
        />
      )}
      {overlay?.type === 'reveal' && (
        <RevealAssignmentsOverlay gameId={game.id} onClose={() => setOverlay(null)} />
      )}
      {/* Snow: opened only via the invite-code trigger in the wishlist
          form (creator); its pairing data is fetched on open. */}
      {overlay?.type === 'snow' && (
        <SnowOverlay gameId={game.id} onClose={() => setOverlay(null)} />
      )}
    </section>
  );
}
